import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Compact 304/316 material tabs shown directly under the product title.
 *
 * Uses the existing /products/:id/related-materials endpoint to find sibling
 * families with the same article-group key. Clicking a tab navigates to that
 * family while preserving the `?dn=` context.
 */
export default function MaterialSwitchTabs({ productId, currentMaterial, dnContext }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!productId) return;
    api.get(`/products/${productId}/related-materials`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [productId]);

  if (!data) return null;

  // Build tab list = current + siblings, sorted: 304 → 316 → others
  const order = ["304", "316", "duplex", "super-duplex", "galvanized", "black"];
  const all = [
    { id: data.current.id, material: data.current.material, current: true },
    ...data.siblings.map((s) => ({ id: s.id, material: s.material, current: false })),
  ];
  // Dedupe by material — keep the first occurrence (current wins, since it's
  // listed first). Multiple sibling families in the same material are merged
  // into one tab; clicking that tab navigates to the canonical sibling.
  const seenMats = new Set();
  const deduped = [];
  for (const m of all) {
    const key = (m.material || "").toLowerCase();
    if (seenMats.has(key)) continue;
    seenMats.add(key);
    deduped.push(m);
  }
  if (deduped.length <= 1) return null;        // nothing to switch to
  deduped.sort((a, b) => {
    const ai = order.indexOf((a.material || "").toLowerCase());
    const bi = order.indexOf((b.material || "").toLowerCase());
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const labelFor = (mat) => {
    const m = (mat || "").toLowerCase();
    if (m === "304") return "304 (V2A)";
    if (m === "316") return "316 (V4A)";
    if (m === "duplex") return "Duplex 1.4462";
    return mat?.toUpperCase() || mat;
  };

  return (
    <div className="mt-3" data-testid="material-switch-tabs">
      <div className="label-uppercase mb-1.5 text-zinc-500">Materiaal</div>
      <div role="tablist" className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 p-0.5">
        {deduped.map((m) => {
          const active = m.material === currentMaterial;
          const href = `/products/${m.id}${dnContext ? `?dn=${dnContext}` : ""}`;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={active}
              key={m.material}
              onClick={() => {
                if (active) return;
                navigate(href);
              }}
              data-testid={`material-tab-${m.material}`}
              className={`min-w-[90px] rounded px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition ${
                active
                  ? "bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-300"
                  : "text-zinc-500 hover:bg-white/60 hover:text-zinc-800"
              }`}
            >
              {labelFor(m.material)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
